import BulkActionsView from 'components/bulk/BulkActionsView';
import BulkMessageView from 'components/bulk/bulk-message/BulkMessageView';
import { Button } from 'components/elements';
import { SectionTitle } from 'components/general-styles';
import Icon from 'components/icon';
import { isReviewList4 } from 'controllers/review/review-controller';
import FeatureManager from 'lib/feature/feature';
import { delay, lowerCase, startCase } from 'lodash';
import { PropTypes } from 'prop-types';
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import Slider from 'react-slick';
import { ChartContext } from 'state/contexts/chart';
import { FeaturesContext } from 'state/contexts/features';
import { PatientContext } from 'state/contexts/patient';
import { ReviewLists4Context } from 'state/contexts/reviewLists4';
import { ActionTypes as ChartActionTypes } from 'state/reducers/chart';
import { ActionTypes, selectVisibleMembers } from 'state/reducers/patient';
import { config } from './config';
import ReviewListClinicFilter from './ReviewListClinicFilter';
import { Content, Counter, Item, Pagination, Section } from './style';

const ReviewList = ({ reviewListLabel, reviewListName }) => {
  const { patientState, patientDispatch } = useContext(PatientContext);
  const { reviewLists4State } = useContext(ReviewLists4Context);
  const { featuresState } = useContext(FeaturesContext);
  const { chartDispatch } = useContext(ChartContext);

  // Only the members of the selected clinic (all of them without a clinic filter)
  const patients = selectVisibleMembers(patientState);
  const numberOfPatients = patients.length;
  const { clinicFilter } = patientState;

  const sliderRef = useRef(null);
  const currentPatientIndex = useRef(0);
  // Derived on every render: the clinic filter changes the visible list and the current member
  currentPatientIndex.current = Math.max(
    patients.findIndex((patient) => patient.current),
    0
  );
  // Fixed per carousel mount (see `key` below): react-slick re-styles its track on any prop change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initialSlide = useMemo(() => currentPatientIndex.current, [clinicFilter]);

  const handleMoveBetweenPatients = useCallback(
    (direction) => {
      const newIndex =
        direction === 'prev' ? currentPatientIndex.current - 1 : currentPatientIndex.current + 1;

      if (newIndex < 0 || newIndex > numberOfPatients - 1) {
        return;
      }

      if (direction === 'prev') {
        sliderRef.current.slickPrev();
      } else {
        sliderRef.current.slickNext();
      }

      currentPatientIndex.current = newIndex;

      patientDispatch({ type: ActionTypes.SELECT, payload: { id: patients[newIndex].id } });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [patients, patientDispatch]
  );
  const handleMoveBetweenPatientsRef = useRef(handleMoveBetweenPatients);
  handleMoveBetweenPatientsRef.current = handleMoveBetweenPatients;

  const handleSelectPatient = (id) => {
    if (id !== patients[currentPatientIndex.current].id) {
      const index = patients.findIndex((patient) => patient.id === id);
      currentPatientIndex.current = index;

      chartDispatch({ type: ChartActionTypes.CLEAR });

      patientDispatch({ type: ActionTypes.SELECT, payload: { id } });
    }
  };

  // Advance after a member is marked as reviewed. Only `slide` triggers it: `slide` stays
  // set afterwards, so reacting to list changes (e.g. the clinic filter) would skip members.
  useEffect(() => {
    if (patientState.slide) {
      delay(() => handleMoveBetweenPatientsRef.current('next'), 50);
    }
  }, [patientState.slide]);

  return (
    <Section>
      <div style={{ marginTop: 10 }}>
        {FeatureManager.isBulkMessagesEnabledForReviewList({
          reviewListSlug: reviewListName,
          featuresState,
        }) && (
          <div style={{ float: 'right' }}>
            <BulkActionsView
              buttonActionLabel='Send Bulk Message'
              rightChildComponent={<BulkMessageView />}
              reviewListName={reviewListName}
            />
          </div>
        )}

        <div style={{ float: 'right', marginRight: 12 }}>
          <ReviewListClinicFilter reviewListLabel={reviewListLabel} />
        </div>

        <SectionTitle>
          {isReviewList4(reviewListName, reviewLists4State.data)
            ? reviewListLabel
            : startCase(lowerCase(reviewListLabel))}
        </SectionTitle>
      </div>

      {numberOfPatients > 0 ? (
        <Content slidesToShow={config.slidesToShow} numberOfPatients={numberOfPatients}>
          {/* Remount per clinic so the carousel starts at the current member of the new list */}
          <Slider
            key={clinicFilter ?? 'all'}
            ref={sliderRef}
            {...config}
            initialSlide={initialSlide}>
            {patients.map((patient) => (
              <Item
                key={`patient-slide-${patient.id}`}
                current={patient.current}
                onClick={() => handleSelectPatient(patient.id)}
                reviewed={patient.reviewed}
                role='button'>
                {patient.avatar?.thumb?.url ? (
                  <img src={patient.avatar.thumb.url} alt={patient.first_name} />
                ) : (
                  <Icon icon='userBubble' color='turquoise' size={80} />
                )}

                <p>{patient.first_name}</p>
              </Item>
            ))}
          </Slider>

          <Pagination>
            <Counter>{`${currentPatientIndex.current + 1}/${numberOfPatients}`}</Counter>
            <Button
              onClick={() => handleMoveBetweenPatients('prev')}
              disabled={currentPatientIndex.current === 0}>
              Previous
            </Button>
            <Button
              onClick={() => handleMoveBetweenPatients('next')}
              disabled={currentPatientIndex.current + 1 === numberOfPatients}>
              Next
            </Button>
          </Pagination>
        </Content>
      ) : (
        <div>There are no patients waiting for review.</div>
      )}
    </Section>
  );
};

ReviewList.propTypes = {
  reviewListLabel: PropTypes.string.isRequired,
  reviewListName: PropTypes.string.isRequired,
};

ReviewList.default = {};

export default ReviewList;
